import React, { ReactChild, useEffect, useState } from "react"
import { NAVI_LINKS } from "~/data/constants/NaviLink"
import { useRouter } from "next/router"
import { LinkParts } from "~/components/atoms/LinkParts"
import { useSelector } from "react-redux"
import { selectCustomer } from "~/slices/customerSlice"
import { PageKey, PageNaviLink, SimulatorMode } from "~/models/Page"
import { PAGES } from "~/data/constants/Page"
import { selectPage } from "~/slices/pageSlice"

/*
    料金シミュレート画面のインジケーターコンポーネント
    props
        links:{
            no:インジケーターの番号
            label:インジケーターに表示される文言
            url:クリックした際の遷移先URL
            pcNone:class「pc-display-none」があるかないかの、真偽値
            pcNoneText:上記pcNoneがTrueの場合に表示したい文言
        }の配列
 */

type Nav = {
  link: PageNaviLink,
  disabled: boolean,
  pageKey: string,
}

interface Props {
  children?: ReactChild | ReactChild[],
}

export function NaviLink(props: Props) {
  const { children } = props
  const { query } = useRouter()
  const customerData = useSelector(selectCustomer)
  const mode = query.mode as SimulatorMode
  const currentPage = query.page as PageKey
  const storeIndex = useSelector(selectPage)?.selectStoreIndex
  const store = customerData.stores[storeIndex]
  const [naviLinks, naviLinkState] = useState<Nav[]>([])

  useEffect(() => {
    naviLinkState((NAVI_LINKS[mode] || []).map(pageKey => {
      let disabled = false
      if (PageKey.Recommend === pageKey) {
        // 商品選択は自動車アフターマーケット業界向けサービスがONじゃなければ表示しない
        if (!customerData.company.automotiveAftermarketService) {
          return null;
        }

        disabled = !store.isValid

      } else if (PageKey.EdRecommend === pageKey) {
        // 商品選択(電帳)は電子帳簿保存法改正対応サービスがONじゃなければ表示しない
        if (!customerData.company.electronicDocumentService) {
          return null;
        }

        disabled = !store.isValid

      } else if (PageKey.PpRecommend === pageKey) {
        // 商品選択(プライスボード)は自動車アフターマーケット業界向けサービスがONの場合は表示しない
        if (customerData.company.automotiveAftermarketService) {
          return null;
        }

        // 商品選択(プライスボード)はプライスボード作成支援サービスがONじゃなければ表示しない
        if (!customerData.company.priceBoardService) {
          return null;
        }

        disabled = !store.isValid

      } else if (PageKey.MpRecommend === pageKey) {
        // 商品選択(マーケットプレイス)は自動車アフターマーケット業界向けサービスがONの場合は表示しない
        if (customerData.company.automotiveAftermarketService) {
          return null;
        }

        // 商品選択(マーケットプレイス)はマーケットプレイスがONじゃなければ表示しない
        if (!customerData.company.marketPlace) {
          return null;
        }

        disabled = !store.isValid
      } else if (PageKey.PayRecommend === pageKey) {
        // 商品選択(BLPay)は自動車アフターマーケット業界向けサービスがONの場合は表示しない
        if (customerData.company.automotiveAftermarketService) {
          return null;
        }

        // 商品選択(BLPay)はマーケットプレイスがONじゃなければ表示しない
        if (!customerData.company.broadLeafPay) {
          return null;
        }

        disabled = !store.isValid

      } else if ([PageKey.Contract, PageKey.SimpleEstimate].includes(pageKey as any)) {
        disabled = !store.isValid

      } else if (PageKey.Estimate === pageKey) {
        disabled = !([
          PageKey.AddPack, PageKey.Option, PageKey.Database,
          PageKey.MaService, PageKey.InstSup
        ].every(key => Array.isArray((store.selectData[key] || {}).ids)) && (store.selectData[pageKey] || {}).ids !== null)

      } else {
        disabled = !Array.isArray((store.selectData[pageKey] || {}).ids)
      }
      const link = PAGES[pageKey]
      return {
        link,
        disabled,
        pageKey
      }
    }))
  }, [query, store])

  return (
    <>
      <div className={"indicator"}>
        <hr className="step-border" />
        {naviLinks.filter(navi => navi !== null).map((navi, index) => {
          const url = (navi.pageKey === PageKey.Customer ? "/simulator" : `/simulator/${mode}/${navi.pageKey}`)
          const className = navi.pageKey === currentPage ? "active" : (navi.disabled ? "disabled" : "")
          return (
            <React.Fragment key={url}>
              <LinkParts url={url} disabled={navi.disabled} className={`item ${className}`}>
                <span className="count">{index + 1}</span>
                <span className="step">{navi.link.label}
                  {navi.link.pcNone && <><br className="pc-display-none" />{navi.link.pcNoneText}</>}
                  </span>
              </LinkParts>
            </React.Fragment>
          )
        })}
      </div>
      <div>
        {children}
      </div>
    </>)
}
